"use client";

import clsx from "clsx";
import DynamicCanvasReveal from "./DynamicCanvasReveal";
import { useWebGL2 } from "./useWebGL";

export default function SpotlightDots({
  children,
  withFadeMask = false,
  disabled = false,
  leftGradientX = "30%",
  rightGradientX = "70%",
  leftGradientY = "50%",
  rightGradientY = "50%",
  gradientSize = "40% 60%",
}: {
  children: React.ReactNode;
  withFadeMask?: boolean;
  disabled?: boolean;
  leftGradientX?: string;
  rightGradientX?: string;
  leftGradientY?: string;
  rightGradientY?: string;
  gradientSize?: string;
}) {
  const webGL2Supported = useWebGL2();

  if (disabled) {
    return <>{children}</>;
  }

  // Always render the section wrapper when not disabled — both on SSR and after
  // hydration. This prevents a DOM structure change when webGL2Supported flips
  // from false → true, which would cause a repaint and delay LCP.
  // Only the animation content (canvas + gradients) is conditional.
  return (
    <section
      className={clsx(
        "relative flex lg:flex-row flex-col items-center justify-between gap-4 overflow-clip min-h-fit"
      )}
    >
      {webGL2Supported && (
        <div className="absolute inset-0 z-10 lg:flex justify-evenly opacity-0 animate-fade-in hidden">
          <div
            className="w-1/2 h-full"
            style={
              {
                "--mask-position-x": leftGradientX,
                "--mask-position-y": leftGradientY,
                background:
                  `radial-gradient(ellipse ${gradientSize} at var(--mask-position-x) var(--mask-position-y), rgba(255, 0, 0, 0.15) 0%, rgba(255, 0, 0, 0.05) 30%, rgb(16 16 16 / var(--tw-bg-opacity)) 70%, #101010 100%)`,
                pointerEvents: "none",
              } as React.CSSProperties
            }
          />
          <div
            className="w-1/2 h-full"
            style={
              {
                "--mask-position-x": rightGradientX,
                "--mask-position-y": rightGradientY,
                background:
                  `radial-gradient(ellipse ${gradientSize} at var(--mask-position-x) var(--mask-position-y), rgba(253, 111, 64, 0.15) 0%, rgba(253, 111, 64, 0.05) 30%, rgb(16 16 16 / var(--tw-bg-opacity)) 70%, #101010 100%)`,
                pointerEvents: "none",
              } as React.CSSProperties
            }
          />
        </div>
      )}
      {withFadeMask && (
        <div className="pointer-events-none absolute top-0 left-0 right-0 z-10 h-12 bg-linear-to-b from-[#101010] to-transparent" />
      )}
      {children}
      {webGL2Supported && (
        <div className="absolute w-full h-full inset-0 lg:flex items-center justify-center opacity-50 hidden">
          <DynamicCanvasReveal
            animationSpeed={0.5}
            containerClassName="bg-transparent"
            colors={[
              [255, 45, 45], // Dark red for outer dots
              [255, 71, 71], // Medium red
              [255, 99, 99], // Light red for center
            ]}
            dotSize={4} // 4x4 pixel dots
            totalSize={14} // Spacing between dots
            opacities={[0.15, 0.2, 0.25, 0.3, 0.35, 0.4, 0.45, 0.5, 0.6, 0.7]}
          />
          {/* Radial gradient overlay for the glowing effect */}
          <div className="absolute inset-0 -z-30 bg-[radial-gradient(ellipse_at_center,rgba(255,45,45,0.15)_0%,rgba(255,45,45,0.05)_30%,rgba(16,16,16,0)_70%)]" />
        </div>
      )}
      {withFadeMask && (
        <div className="pointer-events-none absolute bottom-0 left-0 right-0 z-10 h-12 bg-linear-to-t from-[#101010] to-transparent" />
      )}
    </section>
  );
}
